/**
 * The Test-UI harness controller (TestUI_Architecture_v01 §3) — a pure
 * projection + command-dispatch surface over the already-proven engine.
 *
 * It composes the REAL stack and nothing more:
 *   - the real L3 `AppStore` (`createStore`, @progression/app),
 *   - the production L4 `buildViewModel(state, register, { verdictOf, evidenceOf })`
 *     and the real `createDomRenderer` (@progression/view),
 *   - the production L5 `createI18nProvider` over the shipped catalogs
 *     (@progression/i18n),
 *   - the L6 compute edge `runComputation` + the mock/Pyodide producers
 *     (@progression/host-bundle).
 *
 * On every store change it subscribes, re-derives the view-model and repaints
 * (unidirectional dataflow). It is structurally unable to change a verdict
 * (INV-CORE / INV-STABLE-ID): verdicts always come back from `store.verdictOf`,
 * and it imports app / view / i18n / host-bundle / contracts only — NEVER
 * `@progression/core` (INV-DEP). This is the four-region harness:
 *   1. render pane   — the live, localized view-model tree (DomRenderer);
 *   2. control panel — dispatches Store commands (the test surface);
 *   3. inspector     — provenance / no-equivocation (configDigest + evidence + exposure);
 *   4. log / replay  — event log, undo/redo, snapshot export/import.
 *
 * Function-first: the one visual commitment is dark mode (the default theme);
 * layout stays utilitarian and operable (TestUI brief F).
 */

import { createStore } from '@progression/app';
import type { AppStore } from '@progression/app';
import {
  buildViewModel,
  createDomRenderer,
  buildStylesheet,
  DEFAULT_THEME,
  HIGH_CONTRAST_THEME,
  DARK_THEME,
  DARK_PAGE_BG,
} from '@progression/view';
import type { Theme } from '@progression/view';
import { createI18nProvider, ALL_CATALOGS } from '@progression/i18n';
import {
  runComputation,
  createMockNumpyResource,
  createPyodideNumpyResource,
  snapshotOf,
  seedSnapshot,
  computeSliceVerdicts,
  EXPECTED_SLICE,
} from '@progression/host-bundle';
import type { ComputeOutcome } from '@progression/host-bundle';
import {
  EXT_OBSERVATION_EDGE,
  RESOURCE_EDGE,
  HEURISTIC_EDGE,
  SHARED_FOUNDATION_EDGE,
} from '@progression/contracts/fixtures';
import { messageId, claimId, constraintId } from '@progression/contracts';
import type {
  Claim,
  ClaimId,
  ComputeResource,
  Edge,
  Event,
  HostAdapter,
  I18nProvider,
  Locale,
  Register,
  Renderer,
} from '@progression/contracts';

/** The three selectable themes; the harness defaults to dark (TestUI brief B). */
export type ThemeName = 'dark' | 'light' | 'high-contrast';

const THEMES: Readonly<Record<ThemeName, Theme>> = {
  dark: DARK_THEME,
  light: DEFAULT_THEME,
  'high-contrast': HIGH_CONTRAST_THEME,
};

/** Page background per theme — dark uses the deep-neutral the theme is designed against. */
const PAGE_BG: Readonly<Record<ThemeName, string>> = {
  dark: DARK_PAGE_BG,
  light: '#ffffff',
  'high-contrast': '#ffffff',
};

/** The supported compute predicates (mock `linalg.eigvalsh` spectrum predicates). */
const PREDICATES = ['positive-semidefinite', 'positive-definite', 'all-nonnegative'] as const;

export interface HarnessOptions {
  /** Where to paint (element or selector). Default `#harness-root`. */
  readonly mountPoint?: HTMLElement | string;
  /** Persistence adapter; default: none (in-memory only). */
  readonly adapter?: HostAdapter;
  /** Initial locale. Default `'en'`. */
  readonly locale?: Locale;
  /** Initial register. Default `'full'`. */
  readonly register?: Register;
  /** Initial theme. Default `'dark'` (the one visual commitment). */
  readonly theme?: ThemeName;
  /**
   * Seed event log to replay into the store. Default: the §9.1 seed snapshot.
   * Tests inject their own.
   */
  readonly seedEvents?: readonly Event[];
  /** Compute producer: `'mock'` (default, always available) or `'pyodide'`. */
  readonly resourceMode?: 'mock' | 'pyodide';
  /** Honest isolation tier to surface (the host's report). Default `'BEST-EFFORT'`. */
  readonly isolationTier?: 'STRONG' | 'BEST-EFFORT';
}

const STYLE_ID = 'pv-stylesheet';
const CHROME_STYLE_ID = 'harness-chrome';

export class Harness {
  private readonly store: AppStore;
  private readonly provider: I18nProvider;
  private readonly adapter: HostAdapter | undefined;
  private readonly isolationTier: 'STRONG' | 'BEST-EFFORT';

  private locale: Locale;
  private register: Register;
  private themeName: ThemeName;
  private resourceMode: 'mock' | 'pyodide';
  private selectedClaim: ClaimId | null = null;
  private computeStatus = '';
  private snapshotText = '';
  private newCounter = 0;
  private recordCounter = 0;

  private container: HTMLElement | null = null;
  private renderPane: HTMLElement | null = null;
  private controlsEl: HTMLElement | null = null;
  private inspectorEl: HTMLElement | null = null;
  private logEl: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private renderer: Renderer | null = null;
  private unsubscribe: (() => void) | null = null;

  constructor(options: HarnessOptions = {}) {
    this.store = createStore();
    this.store.replay(options.seedEvents ?? seedSnapshot().events);
    this.provider = createI18nProvider(ALL_CATALOGS);
    this.adapter = options.adapter;
    this.isolationTier = options.isolationTier ?? options.adapter?.isolationTier() ?? 'BEST-EFFORT';
    this.locale = options.locale ?? 'en';
    this.register = options.register ?? 'full';
    this.themeName = options.theme ?? 'dark';
    this.resourceMode = options.resourceMode ?? 'mock';
  }

  // --- lifecycle --------------------------------------------------------------

  /** Build the four-region layout and paint the first frame. */
  mount(host?: HTMLElement | string): void {
    const target = host ?? '#harness-root';
    const el = typeof target === 'string' ? this.resolve(target) : target;
    const doc = el.ownerDocument;
    el.replaceChildren();
    el.classList.add('harness');
    this.container = el;

    this.ensureStylesheets(doc);

    this.renderPane = this.section(doc, 'harness-render', 'Render pane');
    this.controlsEl = this.section(doc, 'harness-controls', 'Control panel');
    this.inspectorEl = this.section(doc, 'harness-inspector', 'Inspector (provenance / no-equivocation)');
    this.logEl = this.section(doc, 'harness-log', 'Log / replay');
    this.statusEl = doc.createElement('div');
    this.statusEl.className = 'harness-statusbar';
    el.append(this.statusEl, this.controlsEl, this.renderPane, this.inspectorEl, this.logEl);

    this.renderer = createDomRenderer({ locale: this.locale, theme: THEMES[this.themeName] });
    this.renderer.mount(this.renderPane);

    this.applyChrome();
    this.unsubscribe = this.store.subscribe(() => {
      this.persist();
      this.renderAll();
    });
    if (this.selectedClaim === null) {
      const first = [...this.store.getState().claims.keys()][0];
      this.selectedClaim = first ?? null;
    }
    this.renderAll();
  }

  destroy(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  // --- the live loop ----------------------------------------------------------

  private renderAll(): void {
    this.paintRenderPane();
    this.renderControls();
    this.renderInspector();
    this.renderLog();
    this.renderStatus();
  }

  private paintRenderPane(): void {
    if (!this.renderPane || !this.renderer) return;
    const vm = buildViewModel(this.store.getState(), this.register, {
      verdictOf: (id) => this.store.verdictOf(id),
      evidenceOf: (id) => this.store.evidenceOf(id),
    });
    // language semantics from L5 — dir flips for RTL, lang + per-script font set.
    this.renderPane.dir = this.provider.dir(this.locale);
    this.renderPane.lang = this.locale;
    this.renderPane.style.fontFamily = this.provider.fontStack(this.provider.scriptOf(this.locale));
    this.renderer.render(vm, this.provider);
  }

  // --- region 2: control panel (dispatch Store commands) ----------------------

  private renderControls(): void {
    const host = this.controlsEl;
    if (!host) return;
    const doc = host.ownerDocument;
    host.replaceChildren();
    host.appendChild(this.heading(doc, 'Control panel'));

    const state = this.store.getState();

    // theme toggle (the one visual commitment)
    host.appendChild(
      this.group(doc, 'Theme', (['dark', 'light', 'high-contrast'] as ThemeName[]).map((name) =>
        this.toggle(doc, name, name === this.themeName, () => this.setTheme(name), `theme-${name}`),
      )),
    );

    // register switch
    host.appendChild(
      this.group(doc, 'Register', (['full', 'plain', 'kid'] as Register[]).map((r) =>
        this.toggle(doc, r, r === this.register, () => this.setRegister(r), `register-${r}`),
      )),
    );

    // locale switch (the shipped catalogs; dir flips for RTL)
    const localeSel = doc.createElement('select');
    localeSel.className = 'harness-select';
    localeSel.dataset['control'] = 'locale';
    for (const cat of ALL_CATALOGS) {
      const opt = doc.createElement('option');
      opt.value = cat.locale;
      opt.textContent = `${cat.locale} (${cat.dir})`;
      if (cat.locale === this.locale) opt.selected = true;
      localeSel.appendChild(opt);
    }
    localeSel.addEventListener('change', () => this.setLocale(localeSel.value));
    host.appendChild(this.group(doc, 'Locale', [localeSel]));

    // Current-Axioms calculus: challenge / activate each constraint
    const consWrap = doc.createElement('div');
    for (const [id, entry] of state.constraints) {
      const row = doc.createElement('div');
      row.className = 'harness-row';
      row.dataset['constraint'] = id;
      const label = doc.createElement('span');
      label.className = 'harness-row-label';
      label.textContent = `${this.t(entry.labelId)} [${entry.status}]`;
      row.append(
        label,
        this.button(doc, 'challenge', () => this.store.dispatch({ c: 'challenge', id }), 'challenge'),
        this.button(doc, 'activate', () => this.store.dispatch({ c: 'activate', id }), 'activate'),
        this.button(doc, 'interpretive', () =>
          this.store.dispatch({ c: 'declare', id, status: 'adopted-interpretive', labelId: entry.labelId }),
          'make-interpretive',
        ),
      );
      consWrap.appendChild(row);
    }
    host.appendChild(this.group(doc, 'Constraints (Current Axioms)', [consWrap]));

    // declare a NEW constraint
    const declBtn = this.button(doc, 'declare new constraint', () => {
      const id = constraintId(`harness-con-${++this.newCounter}`);
      this.store.dispatch({ c: 'declare', id, status: 'adopted-formal', labelId: messageId(`constraint.${id}`) });
    }, 'declare');
    host.appendChild(this.group(doc, 'Declare', [declBtn]));

    // import-edge of each typed kind (constraint flips a verdict; the rest are inert)
    const edges: ReadonlyArray<readonly [string, Edge]> = [
      ['constraint (ext-observation)', EXT_OBSERVATION_EDGE],
      ['resource (inert)', RESOURCE_EDGE],
      ['heuristic (inert)', HEURISTIC_EDGE],
      ['shared-foundation (inert)', SHARED_FOUNDATION_EDGE],
    ];
    host.appendChild(
      this.group(doc, 'Import edge', edges.map(([name, edge]) =>
        this.button(doc, name, () => this.store.dispatch({ c: 'import-edge', edge }), `import-${edge.kind}`),
      )),
    );

    // add a claim
    const addClaimBtn = this.button(doc, 'add claim', () => {
      const n = ++this.newCounter;
      const id = claimId(`harness-claim-${n}`);
      const claim: Claim = {
        id,
        labelId: messageId(`claim.${id}`),
        violates: [],
        needs: [],
        profile: { w: 1, e: 'interp', m: 0 },
      };
      this.store.dispatch({ c: 'add-claim', claim });
      this.selectedClaim = id;
    }, 'add-claim');
    host.appendChild(this.group(doc, 'Claims', [addClaimBtn]));

    host.appendChild(this.computeControl(doc, state.claims));
  }

  private computeControl(doc: Document, claims: ReadonlyMap<ClaimId, Claim>): HTMLElement {
    const wrap = doc.createElement('div');
    wrap.dataset['control'] = 'compute';

    const claimSel = doc.createElement('select');
    claimSel.className = 'harness-select';
    claimSel.dataset['field'] = 'claim';
    for (const id of claims.keys()) {
      const opt = doc.createElement('option');
      opt.value = id;
      opt.textContent = id;
      if (id === this.selectedClaim) opt.selected = true;
      claimSel.appendChild(opt);
    }

    const matrix = doc.createElement('input');
    matrix.type = 'text';
    matrix.className = 'harness-input';
    matrix.dataset['field'] = 'matrix';
    matrix.value = '[[2,0],[0,3]]';

    const predSel = doc.createElement('select');
    predSel.className = 'harness-select';
    predSel.dataset['field'] = 'predicate';
    for (const p of PREDICATES) {
      const opt = doc.createElement('option');
      opt.value = p;
      opt.textContent = p;
      predSel.appendChild(opt);
    }

    // verified base: on `held`, activate this constraint so the kernel re-derives.
    const baseSel = doc.createElement('select');
    baseSel.className = 'harness-select';
    baseSel.dataset['field'] = 'verifiedBase';
    const none = doc.createElement('option');
    none.value = '';
    none.textContent = '(no verified base)';
    baseSel.appendChild(none);
    for (const id of this.store.getState().constraints.keys()) {
      const opt = doc.createElement('option');
      opt.value = id;
      opt.textContent = id;
      baseSel.appendChild(opt);
    }

    const resSel = doc.createElement('select');
    resSel.className = 'harness-select';
    resSel.dataset['field'] = 'resource';
    for (const mode of ['mock', 'pyodide'] as const) {
      const opt = doc.createElement('option');
      opt.value = mode;
      opt.textContent = mode;
      if (mode === this.resourceMode) opt.selected = true;
      resSel.appendChild(opt);
    }
    resSel.addEventListener('change', () => {
      this.resourceMode = resSel.value === 'pyodide' ? 'pyodide' : 'mock';
    });

    const runBtn = this.button(doc, 'run computation', () => {
      void this.runCompute({
        claimRef: claimSel.value as ClaimId,
        matrix: matrix.value,
        predicate: predSel.value,
        verifiedBase: baseSel.value,
      });
    }, 'run-compute');

    wrap.append(claimSel, matrix, predSel, baseSel, resSel, runBtn);
    return this.group(doc, 'Compute (linalg.eigvalsh → evidence → re-derive)', [wrap]);
  }

  // --- region 3: provenance / no-equivocation inspector -----------------------

  private renderInspector(): void {
    const host = this.inspectorEl;
    if (!host) return;
    const doc = host.ownerDocument;
    host.replaceChildren();
    host.appendChild(this.heading(doc, 'Inspector — provenance / no-equivocation'));

    // claim selector
    const sel = doc.createElement('select');
    sel.className = 'harness-select';
    sel.dataset['control'] = 'inspect-claim';
    for (const id of this.store.getState().claims.keys()) {
      const opt = doc.createElement('option');
      opt.value = id;
      opt.textContent = id;
      if (id === this.selectedClaim) opt.selected = true;
      sel.appendChild(opt);
    }
    sel.addEventListener('change', () => {
      this.selectedClaim = sel.value as ClaimId;
      this.renderInspector();
    });
    host.appendChild(sel);

    const id = this.selectedClaim;
    if (id === null || !this.store.getState().claims.has(id)) {
      const none = doc.createElement('p');
      none.textContent = 'No claim selected.';
      host.appendChild(none);
      return;
    }

    const verdict = this.store.verdictOf(id);
    const dl = doc.createElement('dl');
    dl.className = 'harness-kv';
    const kv = (k: string, v: string): void => {
      const dt = doc.createElement('dt');
      dt.textContent = k;
      const dd = doc.createElement('dd');
      dd.textContent = v;
      dl.append(dt, dd);
    };
    kv('verdict.state', verdict.state);
    kv('configDigest', verdict.configDigest);
    kv('conflictingConstraints', verdict.conflictingConstraints.join(', ') || '(none)');
    kv('missingBase', verdict.missingBase.join(', ') || '(none)');
    kv('profile ⟨W,E,M⟩', `⟨${verdict.profile.w}, ${verdict.profile.e}, ${verdict.profile.m}⟩`);
    kv('exposure (interpretive deps)', this.store.exposure(id).join(', ') || '(none)');
    host.appendChild(dl);

    // evidence records — a stale-config result is visibly stale (no_equivocation).
    const evidence = this.store.evidenceOf(id);
    const evHead = doc.createElement('h4');
    evHead.textContent = `Evidence (${evidence.length})`;
    host.appendChild(evHead);
    if (evidence.length === 0) {
      const p = doc.createElement('p');
      p.textContent = 'No computation evidence recorded for this claim.';
      host.appendChild(p);
    } else {
      const ul = doc.createElement('ul');
      ul.className = 'harness-evidence';
      for (const rec of evidence) {
        const li = doc.createElement('li');
        const stale = rec.configDigest !== verdict.configDigest;
        li.dataset['stale'] = String(stale);
        li.textContent =
          `${rec.engine} ${rec.op} → [${Array.isArray(rec.result) ? rec.result.join(', ') : rec.result}] ` +
          `held=${rec.held} refutedProof=${rec.refutedProof} @tick ${rec.recordedAtTick} ` +
          `digest=${rec.configDigest}${stale ? '  ⚠ STALE (config changed since recorded)' : '  ✓ current'}`;
        ul.appendChild(li);
      }
      host.appendChild(ul);
    }
  }

  // --- region 4: log / replay panel -------------------------------------------

  private renderLog(): void {
    const host = this.logEl;
    if (!host) return;
    const doc = host.ownerDocument;
    host.replaceChildren();
    host.appendChild(this.heading(doc, 'Log / replay'));

    const pos = this.store.historyPosition();
    const histLine = doc.createElement('p');
    histLine.dataset['field'] = 'history';
    histLine.textContent = `applied ${pos.applied} · undone ${pos.undone} · events ${pos.events}`;
    host.appendChild(histLine);

    host.appendChild(
      this.group(doc, 'History', [
        this.button(doc, 'undo', () => this.store.dispatch({ c: 'undo' }), 'undo', !pos.canUndo),
        this.button(doc, 'redo', () => this.store.dispatch({ c: 'redo' }), 'redo', !pos.canRedo),
      ]),
    );

    // the §6.2 event log
    const log = this.store.exportLog();
    const ol = doc.createElement('ol');
    ol.className = 'harness-eventlog';
    for (const e of log) {
      const li = doc.createElement('li');
      li.textContent = e.t;
      ol.appendChild(li);
    }
    host.appendChild(this.group(doc, `Event log (${log.length})`, [ol]));

    // snapshot export / import round-trip (replay determinism + L3 round-trip)
    const area = doc.createElement('textarea');
    area.className = 'harness-textarea';
    area.dataset['field'] = 'snapshot';
    area.rows = 4;
    area.value = this.snapshotText;
    area.addEventListener('input', () => {
      this.snapshotText = area.value;
    });
    const exportBtn = this.button(doc, 'export snapshot', () => {
      this.snapshotText = this.store.exportJSON();
      this.renderLog();
    }, 'export');
    const importBtn = this.button(doc, 'import snapshot', () => {
      try {
        this.store.importJSON(this.snapshotText);
        this.computeStatus = 'snapshot imported';
      } catch (err) {
        this.computeStatus = `import failed: ${(err as Error).message}`;
        this.renderStatus();
      }
    }, 'import');
    host.appendChild(this.group(doc, 'Snapshot (export / import)', [area, exportBtn, importBtn]));
  }

  // --- status bar (tier, contract, determinism self-test) ---------------------

  private renderStatus(): void {
    const host = this.statusEl;
    if (!host) return;
    const doc = host.ownerDocument;
    host.replaceChildren();
    const sliceOk =
      JSON.stringify(computeSliceVerdicts(this.store)) === JSON.stringify(EXPECTED_SLICE);
    const line = doc.createElement('span');
    line.dataset['field'] = 'status';
    line.textContent =
      `contract v${this.store.contractVersion()} · isolation ${this.isolationTier} · ` +
      `compute ${this.resourceMode} · §9.1 slice ${sliceOk ? 'OK ✓' : 'MISMATCH ✗'}` +
      (this.computeStatus ? ` · ${this.computeStatus}` : '');
    host.appendChild(line);
  }

  // --- public operations (also the test surface) ------------------------------

  setTheme(name: ThemeName): void {
    this.themeName = name;
    this.applyChrome();
    this.renderAll();
  }

  setRegister(register: Register): void {
    this.register = register;
    this.renderAll();
  }

  setLocale(locale: Locale): void {
    this.locale = locale;
    // the DomRenderer binds its locale at construction — swap it for the new one.
    if (this.renderPane) {
      this.renderer = createDomRenderer({ locale, theme: THEMES[this.themeName] });
      this.renderer.mount(this.renderPane);
    }
    this.renderAll();
  }

  /** Run the compute edge; honest outcome surfaced in the status bar. */
  async runCompute(input: {
    claimRef: ClaimId;
    matrix: string;
    predicate: string;
    verifiedBase: string;
  }): Promise<ComputeOutcome> {
    let matrix: unknown;
    try {
      matrix = JSON.parse(input.matrix);
    } catch (err) {
      this.computeStatus = `compute failed: bad matrix (${(err as Error).message})`;
      this.renderStatus();
      return { status: 'failed', reason: 'bad matrix' };
    }
    const resource: ComputeResource =
      this.resourceMode === 'pyodide'
        ? createPyodideNumpyResource(this.isolationTier)
        : createMockNumpyResource(this.isolationTier);
    const verifiedBase = input.verifiedBase ? constraintId(input.verifiedBase) : undefined;
    const outcome = await runComputation(this.store, resource, {
      claimRef: input.claimRef,
      op: 'linalg.eigvalsh',
      inputs: { matrix },
      predicate: input.predicate,
      inputLabelId: messageId('evidence.input.matrix'),
      predicateId: messageId(`predicate.${input.predicate}`),
      recordId: `harness-compute-${++this.recordCounter}`,
      ...(verifiedBase ? { verifiedBase } : {}),
    });
    this.selectedClaim = input.claimRef;
    this.computeStatus =
      outcome.status === 'recorded'
        ? `compute recorded (held=${outcome.held})`
        : `compute ${outcome.status}${'reason' in outcome ? `: ${outcome.reason}` : ''}`;
    // a recorded run dispatched into the store (→ subscription re-rendered); for the
    // non-recording outcomes nothing changed, so refresh the status line explicitly.
    if (outcome.status !== 'recorded') this.renderStatus();
    return outcome;
  }

  /** Direct accessors for tests. */
  getStore(): AppStore {
    return this.store;
  }
  /** Switch the compute producer (also surfaced via the in-UI resource select). */
  setResourceModeForTest(mode: 'mock' | 'pyodide'): void {
    this.resourceMode = mode;
  }
  getThemeName(): ThemeName {
    return this.themeName;
  }
  getLocale(): Locale {
    return this.locale;
  }
  getRegister(): Register {
    return this.register;
  }

  // --- chrome / persistence ---------------------------------------------------

  private applyChrome(): void {
    if (!this.container) return;
    const doc = this.container.ownerDocument;
    const style = doc.getElementById(STYLE_ID);
    if (style) style.textContent = buildStylesheet(THEMES[this.themeName]);
    this.container.dataset['theme'] = this.themeName;
    this.container.style.backgroundColor = PAGE_BG[this.themeName];
    this.container.style.color = 'var(--pv-neutral)';
  }

  private persist(): void {
    if (!this.adapter) return;
    void this.adapter.save(snapshotOf(this.store));
  }

  private ensureStylesheets(doc: Document): void {
    if (!doc.getElementById(STYLE_ID)) {
      const style = doc.createElement('style');
      style.id = STYLE_ID;
      style.textContent = buildStylesheet(THEMES[this.themeName]);
      (doc.head ?? doc.documentElement).appendChild(style);
    }
    if (!doc.getElementById(CHROME_STYLE_ID)) {
      const chrome = doc.createElement('style');
      chrome.id = CHROME_STYLE_ID;
      chrome.textContent = HARNESS_CHROME_CSS;
      (doc.head ?? doc.documentElement).appendChild(chrome);
    }
  }

  // --- tiny DOM helpers (utilitarian, function-first) -------------------------

  private t(id: Parameters<I18nProvider['t']>[0]): string {
    return this.provider.t(id, undefined, this.locale);
  }

  private resolve(selector: string): HTMLElement {
    const doc = globalThis.document;
    const el = doc.getElementById(selector.replace(/^#/, '')) ?? doc.querySelector(selector);
    if (!(el instanceof HTMLElement)) throw new Error(`Harness.mount: no element for "${selector}"`);
    return el;
  }

  private section(doc: Document, cls: string, label: string): HTMLElement {
    const el = doc.createElement('section');
    el.className = `harness-section ${cls}`;
    el.setAttribute('aria-label', label);
    return el;
  }

  private heading(doc: Document, text: string): HTMLElement {
    const h = doc.createElement('h3');
    h.className = 'harness-h';
    h.textContent = text;
    return h;
  }

  private group(doc: Document, label: string, children: readonly Node[]): HTMLElement {
    const g = doc.createElement('div');
    g.className = 'harness-group';
    g.setAttribute('role', 'group');
    g.setAttribute('aria-label', label);
    const l = doc.createElement('span');
    l.className = 'harness-group-label';
    l.textContent = label;
    g.appendChild(l);
    for (const c of children) g.appendChild(c);
    return g;
  }

  private button(
    doc: Document,
    text: string,
    onClick: () => void,
    action: string,
    disabled = false,
  ): HTMLButtonElement {
    const b = doc.createElement('button');
    b.type = 'button';
    b.className = 'harness-btn';
    b.textContent = text;
    b.dataset['action'] = action;
    b.disabled = disabled;
    b.addEventListener('click', onClick);
    return b;
  }

  private toggle(
    doc: Document,
    text: string,
    pressed: boolean,
    onClick: () => void,
    action: string,
  ): HTMLButtonElement {
    const b = this.button(doc, text, onClick, action);
    b.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    return b;
  }
}

/** Construct + mount + return — the convenience entry. */
export function mountHarness(options: HarnessOptions = {}): Harness {
  const harness = new Harness(options);
  harness.mount(options.mountPoint);
  return harness;
}

/**
 * The harness's utilitarian chrome (function-first; no visual design beyond dark
 * mode + basic legibility). Colours come from the active theme's `--pv-*` custom
 * properties so a theme swap repaints with no rule change; layout uses logical
 * properties so RTL falls out for free.
 */
const HARNESS_CHROME_CSS = [
  '.harness { display: flex; flex-direction: column; gap: 0.75rem; padding: 0.75rem; font: 14px/1.5 system-ui, sans-serif; min-height: 100%; }',
  '.harness-statusbar { font-size: 12px; opacity: 0.85; padding: 0.35rem 0.5rem; border: 1px solid var(--pv-state-outside); border-radius: 4px; }',
  '.harness-section { border: 1px solid var(--pv-state-outside); border-radius: 6px; padding: 0.6rem 0.75rem; }',
  '.harness-h { margin: 0 0 0.5rem; font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; opacity: 0.8; }',
  '.harness-group { display: flex; flex-wrap: wrap; align-items: center; gap: 0.4rem; margin-block: 0.35rem; }',
  '.harness-group-label { font-size: 12px; opacity: 0.7; min-inline-size: 9rem; }',
  '.harness-row { display: flex; flex-wrap: wrap; align-items: center; gap: 0.4rem; margin-block: 0.2rem; }',
  '.harness-row-label { min-inline-size: 14rem; font-size: 13px; }',
  '.harness-btn { padding: 0.2rem 0.55rem; border: 1px solid var(--pv-state-outside); border-radius: 4px; background: transparent; color: inherit; cursor: pointer; font: inherit; }',
  '.harness-btn[aria-pressed="true"] { border-color: var(--pv-axis-warrant); color: var(--pv-axis-warrant); font-weight: 600; }',
  '.harness-btn:disabled { opacity: 0.45; cursor: default; }',
  '.harness-btn:focus-visible, .harness-select:focus-visible { outline: 3px solid var(--pv-axis-warrant); outline-offset: 2px; }',
  '.harness-select, .harness-input, .harness-textarea { background: transparent; color: inherit; border: 1px solid var(--pv-state-outside); border-radius: 4px; padding: 0.15rem 0.35rem; font: inherit; }',
  '.harness-input { min-inline-size: 10rem; }',
  '.harness-textarea { inline-size: 100%; font-family: ui-monospace, monospace; }',
  '.harness-kv { display: grid; grid-template-columns: max-content 1fr; gap: 0.1rem 0.75rem; margin: 0.4rem 0; font-size: 13px; }',
  '.harness-kv dt { opacity: 0.7; }',
  '.harness-kv dd { margin: 0; font-family: ui-monospace, monospace; word-break: break-all; }',
  '.harness-evidence, .harness-eventlog { margin: 0.3rem 0; padding-inline-start: 1.2rem; font-family: ui-monospace, monospace; font-size: 12px; }',
  '.harness-evidence li[data-stale="true"] { color: var(--pv-state-refuted); }',
  '.harness-evidence li[data-stale="false"] { color: var(--pv-lane-verified); }',
  '.harness-render { min-block-size: 6rem; }',
].join('\n');
